import * as types from "./teamTemplateSizesTypes";
import { TeamTemplateSize } from "./teamTemplateSizesActions";

export interface TeamTemplateSizesState {
  entries: Record<string, TeamTemplateSize>;
  teamId: string | null;
  isFetching: boolean;
  isProcessing: boolean;
  lastFetched: number | null;
  errors: Record<string, string>;
}

export const initState: TeamTemplateSizesState = {
  entries: {},
  teamId: null,
  isFetching: false,
  isProcessing: false,
  lastFetched: null,
  errors: {}
};

interface Action {
  type: string;
  response?: {
    ids?: string | string[];
    entities?: {
      teamTemplateSize?: Record<string, TeamTemplateSize>;
    };
  };
  error?: string | { error?: string; message?: string };
  extra?: { sizeId?: string; teamId?: string; pageSize?: number };
}

const readError = (error: Action["error"]): string => {
  if (!error) return "Unknown error";
  if (typeof error === "string") return error;
  return error.error || error.message || "Unknown error";
};

const isStaleTeamResponse = (
  action: Action,
  state: TeamTemplateSizesState
): boolean => {
  const responseTeamId = action.extra?.teamId ?? null;
  return responseTeamId !== null && responseTeamId !== state.teamId;
};

const teamTemplateSizesReducer = (
  state: TeamTemplateSizesState = initState,
  action: Action
): TeamTemplateSizesState => {
  switch (action.type) {
    case types.FETCH_TEAM_TEMPLATE_SIZES_REQUEST: {
      const requestedTeamId = action.extra?.teamId ?? null;
      const isTeamSwitch =
        requestedTeamId !== null &&
        state.teamId !== null &&
        state.teamId !== requestedTeamId;
      return {
        ...state,
        entries: isTeamSwitch ? {} : state.entries,
        teamId: requestedTeamId ?? state.teamId,
        lastFetched: isTeamSwitch ? null : state.lastFetched,
        isFetching: true,
        errors: { ...state.errors, fetch: "" }
      };
    }

    case types.FETCH_TEAM_TEMPLATE_SIZES_SUCCESS: {
      const responseTeamId = action.extra?.teamId ?? null;
      if (responseTeamId !== null && responseTeamId !== state.teamId) {
        // Stale response from a previous team; drop it.
        return state;
      }
      const entities = action.response?.entities?.teamTemplateSize ?? {};
      const ids = action.response?.ids;
      const pageSize = action.extra?.pageSize;
      const isFinalPage =
        !Array.isArray(ids) ||
        typeof pageSize !== "number" ||
        ids.length < pageSize;
      return {
        ...state,
        entries: {
          ...state.entries,
          ...entities
        },
        isFetching: !isFinalPage,
        lastFetched: isFinalPage ? Date.now() : state.lastFetched
      };
    }

    case types.FETCH_TEAM_TEMPLATE_SIZES_FAILURE: {
      const responseTeamId = action.extra?.teamId ?? null;
      if (responseTeamId !== null && responseTeamId !== state.teamId) {
        // Stale response from a previous team; drop it.
        return state;
      }
      return {
        ...state,
        isFetching: false,
        errors: { ...state.errors, fetch: readError(action.error) }
      };
    }

    case types.CREATE_TEAM_TEMPLATE_SIZE_REQUEST:
      return {
        ...state,
        isProcessing: true,
        errors: { ...state.errors, create: "" }
      };
    case types.UPDATE_TEAM_TEMPLATE_SIZE_REQUEST:
      return {
        ...state,
        isProcessing: true,
        errors: { ...state.errors, update: "" }
      };
    case types.DELETE_TEAM_TEMPLATE_SIZE_REQUEST:
      return {
        ...state,
        isProcessing: true,
        errors: { ...state.errors, delete: "" }
      };

    case types.CREATE_TEAM_TEMPLATE_SIZE_SUCCESS:
    case types.UPDATE_TEAM_TEMPLATE_SIZE_SUCCESS: {
      if (isStaleTeamResponse(action, state)) {
        // Drop a mutation response from a previous team so it doesn't
        // leak entries into the current team's list.
        return { ...state, isProcessing: false };
      }
      const entities = action.response?.entities?.teamTemplateSize ?? {};
      return {
        ...state,
        entries: {
          ...state.entries,
          ...entities
        },
        isProcessing: false
      };
    }

    case types.DELETE_TEAM_TEMPLATE_SIZE_SUCCESS: {
      if (isStaleTeamResponse(action, state)) {
        return { ...state, isProcessing: false };
      }
      const sizeId = action.extra?.sizeId;
      if (!sizeId) {
        return { ...state, isProcessing: false };
      }
      const { [sizeId]: _removed, ...remaining } = state.entries;
      return {
        ...state,
        entries: remaining,
        isProcessing: false
      };
    }

    case types.CREATE_TEAM_TEMPLATE_SIZE_FAILURE: {
      if (isStaleTeamResponse(action, state)) {
        return { ...state, isProcessing: false };
      }
      return {
        ...state,
        isProcessing: false,
        errors: { ...state.errors, create: readError(action.error) }
      };
    }
    case types.UPDATE_TEAM_TEMPLATE_SIZE_FAILURE: {
      if (isStaleTeamResponse(action, state)) {
        return { ...state, isProcessing: false };
      }
      return {
        ...state,
        isProcessing: false,
        errors: { ...state.errors, update: readError(action.error) }
      };
    }
    case types.DELETE_TEAM_TEMPLATE_SIZE_FAILURE: {
      if (isStaleTeamResponse(action, state)) {
        return { ...state, isProcessing: false };
      }
      return {
        ...state,
        isProcessing: false,
        errors: { ...state.errors, delete: readError(action.error) }
      };
    }

    default:
      return state;
  }
};

export default teamTemplateSizesReducer;
